import React, { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar as MuiAppBar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { ReactComponent as FullLogoSVG } from "../../static/svg/full-logo.svg";
import { ReactComponent as StackedLogoSVG } from "../../static/svg/logo.svg";
import { navItems } from "../../utils/testData";
import { useCart } from "../../context/cart-context";
import { useWishlist } from "../../context/wishlist-context";
import { AuthContext } from "../../context/auth-context";
import { signOutUser } from "../../firebase/firebaseAuth";

const BRAND = "#194049";
const BRAND_HOVER = "rgba(255,255,255,0.08)";

export const AppBar = () => {
  const { isAuthenticated, logoutUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { cart } = useCart();
  const { wishlist } = useWishlist();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState(null);

  const accountOpen = Boolean(accountAnchor);

  const handleLogout = async () => {
    try {
      await signOutUser();
      logoutUser();
      setAccountAnchor(null);
      navigate("/");
    } catch (err) {
      console.error(err);
    }
  };

  const isActive = (path) => location.pathname === path;

  const navLinkSx = (path) => ({
    fontSize: 13,
    fontWeight: 500,
    color: "#fff",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    px: 1.5,
    py: 0.5,
    borderRadius: "6px",
    textDecoration: "none",
    borderBottom: isActive(path) ? "2px solid #fff" : "2px solid transparent",
    opacity: isActive(path) ? 1 : 0.8,
    transition: "all 0.15s",
    "&:hover": { opacity: 1, bgcolor: BRAND_HOVER },
  });

  return (
    <>
      <MuiAppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: BRAND, borderBottom: "1px solid rgba(255,255,255,0.1)" }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, px: { xs: 2, md: 4, lg: 8 }, justifyContent: "space-between" }}>

          {/* Left: hamburger (mobile) + Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "#fff", mr: 0.5 }} size="small">
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/" style={{ display: "flex", alignItems: "center" }}>
              {isMobile
                ? <StackedLogoSVG width="36px" height="36px" />
                : <FullLogoSVG width="140px" height="44px" />
              }
            </Link>
          </Box>

          {/* Center: nav links (desktop only) */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {navItems.map((item) => (
                <Box key={item.path} component={Link} to={item.path} sx={navLinkSx(item.path)}>
                  {item.name}
                </Box>
              ))}
            </Box>
          )}

          {/* Right: wishlist + cart + account */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* Wishlist */}
            <IconButton component={Link} to="/wishlist" sx={{ color: "#fff" }} size="small" title="Wishlist">
              <Badge
                badgeContent={wishlist.length || null}
                color="secondary"
                sx={{ "& .MuiBadge-badge": { fontSize: 10, minWidth: 16, height: 16 } }}
              >
                {isActive("/wishlist")
                  ? <FavoriteIcon sx={{ fontSize: 22 }} />
                  : <FavoriteBorderIcon sx={{ fontSize: 22 }} />
                }
              </Badge>
            </IconButton>

            {/* Cart */}
            <IconButton component={Link} to="/cart" sx={{ color: "#fff" }} size="small" title="Cart">
              <Badge
                badgeContent={cart.length}
                color="secondary"
                sx={{ "& .MuiBadge-badge": { fontSize: 10, minWidth: 16, height: 16 } }}
              >
                <ShoppingBagOutlinedIcon sx={{ fontSize: 22 }} />
              </Badge>
            </IconButton>

            {/* Account */}
            <IconButton
              onClick={(e) => setAccountAnchor(e.currentTarget)}
              sx={{
                color: "#fff",
                bgcolor: accountOpen ? BRAND_HOVER : "transparent",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "8px",
                px: 1.25,
                py: 0.5,
                gap: 0.75,
                fontSize: 13,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                ml: 0.5,
                "&:hover": { bgcolor: BRAND_HOVER },
              }}
              disableRipple={false}
            >
              <AccountCircleOutlinedIcon sx={{ fontSize: 20 }} />
              {!isMobile && (
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>
                  {isAuthenticated ? "Account" : "Log In"}
                </Typography>
              )}
            </IconButton>
          </Box>
        </Toolbar>
      </MuiAppBar>

      {/* Account dropdown menu */}
      <Menu
        anchorEl={accountAnchor}
        open={accountOpen}
        onClose={() => setAccountAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 4,
          sx: { mt: 1, minWidth: 180, borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" },
        }}
      >
        {isAuthenticated ? (
          [
            <MenuItem key="dashboard" component={Link} to="/dashboard" onClick={() => setAccountAnchor(null)} sx={{ fontSize: 14, gap: 1.5, py: 1.25 }}>
              <DashboardOutlinedIcon sx={{ fontSize: 18, color: BRAND }} />
              Dashboard
            </MenuItem>,
            <Divider key="div" />,
            <MenuItem key="logout" onClick={handleLogout} sx={{ fontSize: 14, gap: 1.5, py: 1.25, color: "#ef4444" }}>
              <LogoutIcon sx={{ fontSize: 18, color: "#ef4444" }} />
              Sign Out
            </MenuItem>,
          ]
        ) : (
          [
            <MenuItem key="login" component={Link} to="/login" onClick={() => setAccountAnchor(null)} sx={{ fontSize: 14, gap: 1.5, py: 1.25 }}>
              <LoginOutlinedIcon sx={{ fontSize: 18, color: BRAND }} />
              Log In
            </MenuItem>,
            <Divider key="div" />,
            <MenuItem key="signup" component={Link} to="/signup" onClick={() => setAccountAnchor(null)} sx={{ fontSize: 14, gap: 1.5, py: 1.25 }}>
              <PersonAddOutlinedIcon sx={{ fontSize: 18, color: BRAND }} />
              Sign Up
            </MenuItem>,
          ]
        )}
      </Menu>

      {/* Mobile drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260, bgcolor: BRAND, color: "#fff" } }}
      >
        {/* Drawer header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <FullLogoSVG width="110px" height="36px" />
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: "#fff" }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Nav links */}
        <List sx={{ pt: 1.5 }}>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  mx: 1.5,
                  borderRadius: "8px",
                  mb: 0.5,
                  bgcolor: isActive(item.path) ? "rgba(255,255,255,0.15)" : "transparent",
                  "&:hover": { bgcolor: BRAND_HOVER },
                }}
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: isActive(item.path) ? 600 : 400, color: "#fff", textTransform: "uppercase", letterSpacing: "0.06em" }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mx: 2, my: 1 }} />

        {/* Wishlist + Cart quick links */}
        <List sx={{ py: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/wishlist"
              onClick={() => setDrawerOpen(false)}
              sx={{ mx: 1.5, borderRadius: "8px", mb: 0.5, bgcolor: isActive("/wishlist") ? "rgba(255,255,255,0.15)" : "transparent", "&:hover": { bgcolor: BRAND_HOVER } }}
            >
              {isActive("/wishlist")
                ? <FavoriteIcon sx={{ fontSize: 18, color: "#fca5a5", mr: 1.5 }} />
                : <FavoriteBorderIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)", mr: 1.5 }} />
              }
              <ListItemText
                primary={wishlist.length > 0 ? `Wishlist (${wishlist.length})` : "Wishlist"}
                primaryTypographyProps={{ fontSize: 14, color: "#fff" }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mx: 2, my: 1 }} />

        {/* Auth links */}
        <List>
          {isAuthenticated ? (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/dashboard"
                  onClick={() => setDrawerOpen(false)}
                  sx={{ mx: 1.5, borderRadius: "8px", mb: 0.5, "&:hover": { bgcolor: BRAND_HOVER } }}
                >
                  <DashboardOutlinedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)", mr: 1.5 }} />
                  <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: 14, color: "#fff" }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => { setDrawerOpen(false); handleLogout(); }}
                  sx={{ mx: 1.5, borderRadius: "8px", "&:hover": { bgcolor: "rgba(239,68,68,0.15)" } }}
                >
                  <LogoutIcon sx={{ fontSize: 18, color: "#fca5a5", mr: 1.5 }} />
                  <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: 14, color: "#fca5a5" }} />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/login"
                  onClick={() => setDrawerOpen(false)}
                  sx={{ mx: 1.5, borderRadius: "8px", mb: 0.5, "&:hover": { bgcolor: BRAND_HOVER } }}
                >
                  <LoginOutlinedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)", mr: 1.5 }} />
                  <ListItemText primary="Log In" primaryTypographyProps={{ fontSize: 14, color: "#fff" }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/signup"
                  onClick={() => setDrawerOpen(false)}
                  sx={{ mx: 1.5, borderRadius: "8px", "&:hover": { bgcolor: BRAND_HOVER } }}
                >
                  <PersonAddOutlinedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)", mr: 1.5 }} />
                  <ListItemText primary="Sign Up" primaryTypographyProps={{ fontSize: 14, color: "#fff" }} />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
};
